import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Brand from './components/Brand';
import ThemeToggle from './components/ThemeToggle';

function CaseStudies() {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleNavigation = (hash: string) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-surface-2/90 backdrop-blur-sm border-b border-line z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px]">
            <Brand />

            <div className="flex items-center gap-4">
            <div className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/')} className="text-content-2 hover:text-accent font-medium transition">Home</button>
              <button onClick={() => handleNavigation('solutions')} className="text-content-2 hover:text-accent font-medium transition">Solutions</button>
              <button onClick={() => handleNavigation('industries')} className="text-content-2 hover:text-accent font-medium transition">Industries</button>
              <Link to="/case-studies" className="text-accent font-medium">Case Studies</Link>
              <button onClick={() => handleNavigation('about')} className="text-content-2 hover:text-accent font-medium transition">About</button>
              <button onClick={() => handleNavigation('contact')} className="text-content-2 hover:text-accent font-medium transition">Contact</button>
            </div>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-surface-2 to-surface-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-content mb-4">
              Case Studies
            </h1>
            <p className="text-xl text-content-2 max-w-3xl mx-auto">
              Real-world examples of transformative solutions we've delivered across industries
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Content */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Fortune 500 Retail Pricing Platform */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-accent font-semibold">Fortune 500 Retail</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Global Pricing & Promotion Management Platform</h3>
                  <p className="text-content-2 mb-6">
                    We built a comprehensive pricing platform that empowers central teams to orchestrate 
                    thousands of SKUs across global stores and e-commerce channels. We enabled real-time 
                    multi-currency pricing and built intelligent systems that analyze billions of promotion 
                    combinations, preventing conflicts before they reach POS systems.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Multi-currency, location-specific pricing</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Conflict detection across billions of combinations</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Reduced pricing conflicts from 10+ hours to real-time</span>
                    </div>
                  </div>
                </div>
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">1000s</div>
                      <div className="text-content-2">SKUs Managed</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">100%</div>
                      <div className="text-content-2">Pricing Confidence</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">10x</div>
                      <div className="text-content-2">Performance Gain</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">100s</div>
                      <div className="text-content-2">Concurrent Promotions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cannabis Startup */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 md:order-1 bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">100+</div>
                      <div className="text-content-2">Stores in 4 Months</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">100x</div>
                      <div className="text-content-2">Growth Rate</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Real-time</div>
                      <div className="text-content-2">Inventory Sync</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Full</div>
                      <div className="text-content-2">3PL Integration</div>
                    </div>
                  </div>
                </div>
                <div className="order-1 md:order-2">
                  <span className="text-accent font-semibold">Cannabis Startup</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Rapid Retail Expansion Platform</h3>
                  <p className="text-content-2 mb-6">
                    We enabled a CBD retailer to achieve explosive growth from 1 to 100+ stores in just 4 months. 
                    We built a centralized command center for POS data, inventory management, and automated 
                    replenishment. We created a custom PIM system with seamless Shopify integration, enabling 
                    real-time product updates, stock synchronization, and order fulfillment tracking.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Centralized POS & inventory hub</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Custom PIM with Shopify integration</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Automated 3PL shipping & tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Startup */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-accent font-semibold">AI Startup</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">AI Document-Intelligence Platform</h3>
                  <p className="text-content-2 mb-6">
                    We partnered with an early-stage team to build the AI engine behind their
                    document-intelligence platform. We engineered the pipeline that ingests massive
                    volumes of unstructured documents, runs multi-stage LLM analysis with automated
                    quality scoring, and returns evidence-backed findings with citations and complete
                    audit trails, reliably and at scale.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Multi-format document processing</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Evidence-based finding generation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">LLM-as-judge quality scoring</span>
                    </div>
                  </div>
                </div>
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">1000s</div>
                      <div className="text-content-2">Documents Analyzed</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multiple</div>
                      <div className="text-content-2">Data Sources</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Evidence</div>
                      <div className="text-content-2">Based Findings</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Full</div>
                      <div className="text-content-2">Audit Trails</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Governed AI for Audit & Controls */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-1 md:order-2">
                  <span className="text-accent font-semibold">AI Governance Platform</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Governed AI for Audit &amp; Controls</h3>
                  <p className="text-content-2 mb-6">
                    We engineered an AI platform that runs complex audit and controls procedures end to
                    end. Multi-step orchestration plans each engagement, executes governed analytical
                    procedures, and applies LLM-as-judge evaluation to assess findings against defined
                    assertions. Every conclusion is backed by linked evidence and a complete, replayable
                    audit trail.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Orchestrated, multi-step governed procedures</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">LLM-as-judge assertion evaluation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Evidence-linked findings with full traceability</span>
                    </div>
                  </div>
                </div>
                <div className="order-2 md:order-1 bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multi-Step</div>
                      <div className="text-content-2">Orchestration</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">LLM-as-Judge</div>
                      <div className="text-content-2">Evaluation</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">End-to-End</div>
                      <div className="text-content-2">Audit Trails</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Evidence</div>
                      <div className="text-content-2">Backed Findings</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AI-Driven SOX Compliance */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="order-2 bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">SOX</div>
                      <div className="text-content-2">Controls Automated</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">AI</div>
                      <div className="text-content-2">Judgement</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Full</div>
                      <div className="text-content-2">Evidence Tracking</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">End-to-End</div>
                      <div className="text-content-2">Traceability</div>
                    </div>
                  </div>
                </div>
                <div className="order-1">
                  <span className="text-accent font-semibold">Enterprise Compliance</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">AI-Driven SOX Compliance</h3>
                  <p className="text-content-2 mb-6">
                    We built the AI engine behind a SOX compliance platform that automates control
                    testing and evidence collection. It orchestrates testing workflows, applies LLM
                    judgement to evaluate control effectiveness, tracks every piece of supporting
                    evidence, and preserves end-to-end traceability from control to conclusion.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Automated SOX control testing</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">AI judgement on control effectiveness</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Evidence tracking with end-to-end traceability</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Retailer POS */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">1000s</div>
                      <div className="text-content-2">Store Locations</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Next-Gen</div>
                      <div className="text-content-2">POS Platform</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Real-time</div>
                      <div className="text-content-2">Data Sync</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Zero</div>
                      <div className="text-content-2">Downtime</div>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-accent font-semibold">Major Retailer</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Next-Generation POS Rollout</h3>
                  <p className="text-content-2 mb-6">
                    Led the technical implementation of a next-generation point-of-sale system across thousands of retail locations. 
                    We orchestrated complex data migrations, implemented real-time inventory synchronization, and ensured zero-downtime 
                    deployment. Our solution included automated testing frameworks and monitoring systems to guarantee system reliability 
                    during the critical transition period.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Enterprise-wide POS deployment</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Automated data migration & orchestration</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Zero-downtime deployment strategy</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Warehouse Modernization */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-accent font-semibold">Fortune 500 Enterprise</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Data Warehouse Modernization</h3>
                  <p className="text-content-2 mb-6">
                    Transformed a legacy Oracle/Teradata data warehouse into a modern cloud-native solution. We achieved 10x performance 
                    improvements while reducing operational costs by 60%. The migration included complex ETL pipeline redesign, 
                    implementation of real-time data streaming, and development of advanced analytics capabilities using modern BI tools.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Oracle/Teradata to cloud migration</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">10x query performance improvement</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">60% cost reduction achieved</span>
                    </div>
                  </div>
                </div>
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">10x</div>
                      <div className="text-content-2">Performance Gain</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">60%</div>
                      <div className="text-content-2">Cost Reduction</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">PB+</div>
                      <div className="text-content-2">Data Migrated</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Real-time</div>
                      <div className="text-content-2">Analytics</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Blockchain Integration */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">20+</div>
                      <div className="text-content-2">Engineers Managed</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Global</div>
                      <div className="text-content-2">Team Coordination</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multiple</div>
                      <div className="text-content-2">POCs Delivered</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Enterprise</div>
                      <div className="text-content-2">Integration</div>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-accent font-semibold">International Blockchain Company</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Enterprise Blockchain Integration</h3>
                  <p className="text-content-2 mb-6">
                    As CTO, managed a distributed team of 20+ engineers across Europe to deliver enterprise blockchain solutions. 
                    Developed multiple proof-of-concepts demonstrating seamless integration between traditional enterprise systems 
                    and blockchain networks. Created open-source tools to accelerate corporate adoption of distributed ledger technology.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Enterprise-blockchain bridge development</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Open-source onboarding tools</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">International team leadership</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Theme Park CRM */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-accent font-semibold">Entertainment Industry</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Theme Park Guest Experience Platform</h3>
                  <p className="text-content-2 mb-6">
                    Designed a comprehensive CRM solution for one of the world's largest theme park operators. The system orchestrates 
                    the entire guest journey from booking through post-visit engagement. We created integrated experiences spanning 
                    transportation, lodging, park navigation, attraction queuing, and dining reservations, all unified through a 
                    single guest profile system.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">End-to-end guest journey mapping</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Unified guest profile system</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Multi-touchpoint integration</span>
                    </div>
                  </div>
                </div>
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Millions</div>
                      <div className="text-content-2">Annual Guests</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">360°</div>
                      <div className="text-content-2">Guest View</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multiple</div>
                      <div className="text-content-2">Park Integration</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Real-time</div>
                      <div className="text-content-2">Experience</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SaaS Platform */}
            <div className="bg-surface-3 rounded-2xl border border-line p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-surface-3 p-8 rounded-xl border border-line">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">20+</div>
                      <div className="text-content-2">Years Operating</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Multiple</div>
                      <div className="text-content-2">SaaS Products</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">Large</div>
                      <div className="text-content-2">User Base</div>
                    </div>
                    <div className="bg-accent/10 border border-accent/40 rounded-lg p-4">
                      <div className="text-3xl font-bold text-accent">High</div>
                      <div className="text-content-2">Availability</div>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-accent font-semibold">SaaS Company</span>
                  <h3 className="text-3xl font-bold mt-2 mb-4 text-content">Multi-Tenant SaaS Platform & Mobile Games</h3>
                  <p className="text-content-2 mb-6">
                    Founded and operate a successful SaaS company delivering multiple products including real-time mobile games. 
                    Built a robust microservice infrastructure supporting high-concurrency gaming with global player bases. 
                    The platform handles millions of requests daily with 99.99% uptime, featuring automated scaling, 
                    real-time analytics, and sophisticated player matching algorithms.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Real-time multiplayer infrastructure</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">Microservice architecture at scale</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-content-2">99.99% platform uptime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl font-bold text-content mb-4">Ready to Build Your Success Story?</h2>
            <p className="text-xl text-content-2 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can transform your technical challenges into competitive advantages.
            </p>
            <button onClick={() => handleNavigation('contact')} className="px-8 py-3 bg-accent text-surface font-semibold rounded-lg hover:bg-accent-hover transition shadow-lg">
              Start Your Project
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-2 text-content py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-content-2">© 2017-2026 Databurst Technologies LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CaseStudies;